import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatCurrency, formatDate, getStatusColor, getInvoiceTypeLabel } from '../lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { TemplateSelector } from '../components/TemplateSelector';
import { SendDocumentModal } from '../components/SendDocumentModal';
import { DeliveryHistory } from '../components/DeliveryHistory';
import { ApplyCreditModal } from '../components/ApplyCreditModal';
import { EInvoiceExportModal } from '../components/EInvoiceExportModal';
import { ServiceNotesPanel } from '../components/ServiceNotesPanel';
import { previewDocumentPdf } from '../lib/documentPreview';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, FileText, Calculator, Receipt, CreditCard, DollarSign,
  Trash2, History, Send, Package, Pencil, TrendingUp, BarChart3, RotateCcw, Check, FileCode2, Eye, ClipboardList
} from 'lucide-react';

function nextPositionNumber(lineItems) {
  const nums = (lineItems || []).map((li) => parseInt(li.position_number, 10)).filter((n) => !Number.isNaN(n));
  return nums.length ? Math.max(...nums) + 1 : 1;
}

function localDateInputValue(value = null) {
  if (value) return String(value).slice(0, 10);
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().split('T')[0];
}

function lineRemainingQty(line) {
  const total = parseFloat(line.quantity) || 0;
  const invoiced = parseFloat(line.qty_invoiced) || 0;
  return Math.max(0, Math.round((total - invoiced) * 100) / 100);
}

function documentTimestamp(doc, dateKeys) {
  for (const key of dateKeys) {
    if (doc?.[key]) return new Date(doc[key]).getTime();
  }
  return 0;
}

function latestDocument(docs, dateKeys) {
  if (!docs?.length) return null;
  return [...docs].sort(
    (a, b) => documentTimestamp(b, dateKeys) - documentTimestamp(a, dateKeys)
  )[0];
}

function latestQuote(quotes) {
  return latestDocument(quotes, ['quote_date', 'created_at', 'updated_at']);
}

function latestEstimate(estimates) {
  return latestDocument(estimates, ['estimate_date', 'created_at', 'updated_at']);
}

function defaultSourceForType(sourceType, quotes, estimates) {
  if (sourceType === 'quote') {
    const doc = latestQuote(quotes);
    return { source_type: 'quote', source_id: doc?.id || '', doc };
  }
  if (sourceType === 'estimate') {
    const doc = latestEstimate(estimates);
    return { source_type: 'estimate', source_id: doc?.id || '', doc };
  }
  return { source_type: 'none', source_id: '', doc: null };
}

function usesPositionBilling(invoiceType) {
  return ['progress', 'final', 'milestone'].includes(invoiceType);
}

function usesPercentageBilling(invoiceType) {
  return invoiceType === 'deposit';
}

const DE_B2B_THIRD_COUNTRY_CODE = 'DE_B2B_THIRD_COUNTRY_SERVICE';
const DE_B2B_THIRD_COUNTRY_REASON = 'Nicht im Inland steuerbare sonstige Leistung gemäß § 3a Abs. 2 UStG. Service not subject to German VAT; place of supply is at the business customer’s location.';

function documentTaxTreatment(lineItems = []) {
  return lineItems.some((item) => item.tax_code === DE_B2B_THIRD_COUNTRY_CODE)
    ? 'de_b2b_third_country_service'
    : 'standard';
}

function applyTaxTreatment(item, treatment) {
  if (treatment === 'de_b2b_third_country_service') {
    return {
      ...item,
      taxable_gst: false,
      taxable_pst: false,
      tax_rule_id: null,
      tax_code: DE_B2B_THIRD_COUNTRY_CODE,
      tax_exemption_reason: DE_B2B_THIRD_COUNTRY_REASON,
    };
  }
  return {
    ...item,
    tax_rule_id: item.tax_rule_id || null,
    tax_code: item.tax_code === DE_B2B_THIRD_COUNTRY_CODE ? null : (item.tax_code || null),
    tax_exemption_reason: item.tax_code === DE_B2B_THIRD_COUNTRY_CODE ? '' : (item.tax_exemption_reason || ''),
  };
}

function TaxTreatmentSelector({ value, onChange }) {
  return (
    <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
      <Label>Tax treatment / Steuerliche Behandlung</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="document-tax-treatment"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="standard">Standard taxes / Reguläre Besteuerung</SelectItem>
          <SelectItem value="de_b2b_third_country_service">German B2B service to non-EU business — no German VAT</SelectItem>
        </SelectContent>
      </Select>
      {value === 'de_b2b_third_country_service' && (
        <p className="text-xs text-slate-600">{DE_B2B_THIRD_COUNTRY_REASON}</p>
      )}
    </div>
  );
}

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Dialog states
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [applyCreditInvoice, setApplyCreditInvoice] = useState(null);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  
  // Send modal states
  const [sendQuoteModal, setSendQuoteModal] = useState({ open: false, quoteId: null, quoteNumber: null });
  const [sendEstimateModal, setSendEstimateModal] = useState({ open: false, estimateId: null, estimateNumber: null });
  const [sendInvoiceModal, setSendInvoiceModal] = useState({ open: false, invoiceId: null, invoiceNumber: null });
  const [eInvoiceExport, setEInvoiceExport] = useState(null);
  
  // Edit states
  const [editingQuote, setEditingQuote] = useState(null);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [historyQuoteId, setHistoryQuoteId] = useState(null);
  const [historyEstimateId, setHistoryEstimateId] = useState(null);
  const [historyInvoiceId, setHistoryInvoiceId] = useState(null);
  const [deliveryHistoryVersion, setDeliveryHistoryVersion] = useState(0);
  const [showProjectEdit, setShowProjectEdit] = useState(false);
  const [clients, setClients] = useState([]);

  const fetchAll = useCallback(() => {
    api.get(`/projects/${projectId}`).then(r => {
      setProject(r.data);
      if (r.data?.client_id) {
        api.get(`/credit-notes?customer_id=${r.data.client_id}`)
          .then(cn => setCreditNotes(Array.isArray(cn.data) ? cn.data : []))
          .catch(() => setCreditNotes([]));
      } else {
        setCreditNotes([]);
      }
    });
    api.get('/clients').then(r => setClients(r.data)).catch(() => {});
    api.get(`/quotes?project_id=${projectId}`).then(r => setQuotes(r.data));
    api.get(`/estimates?project_id=${projectId}`).then(r => setEstimates(r.data));
    api.get(`/invoices?project_id=${projectId}`).then(r => setInvoices(r.data));
    api.get(`/payments?project_id=${projectId}`).then(r => setPayments(r.data));
    api.get(`/expenses?project_id=${projectId}`).then(r => setExpenses(r.data));
  }, [projectId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!quotes.length) {
      setHistoryQuoteId(null);
      return;
    }
    if (!historyQuoteId || !quotes.some((quote) => quote.id === historyQuoteId)) {
      setHistoryQuoteId(quotes[0].id);
    }
  }, [quotes, historyQuoteId]);

  useEffect(() => {
    if (!estimates.length) {
      setHistoryEstimateId(null);
      return;
    }
    if (!historyEstimateId || !estimates.some((estimate) => estimate.id === historyEstimateId)) {
      setHistoryEstimateId(estimates[0].id);
    }
  }, [estimates, historyEstimateId]);

  useEffect(() => {
    if (!invoices.length) {
      setHistoryInvoiceId(null);
      return;
    }
    if (!historyInvoiceId || !invoices.some((inv) => inv.id === historyInvoiceId)) {
      setHistoryInvoiceId(invoices[0].id);
    }
  }, [invoices, historyInvoiceId]);

  const handleDocumentSent = () => {
    fetchAll();
    setDeliveryHistoryVersion((version) => version + 1);
  };

  const creditApplications = useMemo(() => {
    if (!invoices.length || !creditNotes.length) return [];
    const invIds = new Set(invoices.map((i) => i.id));
    const rows = [];
    for (const cn of creditNotes) {
      for (const app of cn.applications || []) {
        if (invIds.has(app.invoice_id)) {
          rows.push({
            id: app.id,
            applied_at: app.applied_at,
            amount_applied: app.amount_applied,
            invoice_number: app.invoice_number,
            credit_note_number: cn.credit_note_number,
            credit_note_id: cn.id,
          });
        }
      }
    }
    return rows.sort((a, b) => new Date(b.applied_at) - new Date(a.applied_at));
  }, [invoices, creditNotes]);

  const paymentActivity = useMemo(() => {
    const items = [
      ...payments.map((p) => ({ kind: 'payment', date: p.payment_date, data: p })),
      ...creditApplications.map((c) => ({ kind: 'credit', date: c.applied_at, data: c })),
    ];
    return items.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [payments, creditApplications]);

  if (!project) return <div className="p-8 text-slate-500">Loading...</div>;

  const totalQuoted = quotes.reduce((s, q) => s + parseFloat(q.total || 0), 0);
  const totalEstimated = estimates.reduce((s, e) => s + parseFloat(e.total || 0), 0);
  const nonVoidInvoices = invoices.filter((i) => i.status !== 'void');
  const totalInvoiced = nonVoidInvoices.reduce((s, i) => s + parseFloat(i.total || 0), 0);
  const totalPaid = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const totalOutstanding = nonVoidInvoices.reduce((s, i) => s + parseFloat(i.balance_due || 0), 0);
  const projectProfitLoss = totalInvoiced - totalExpenses;
  const projectMarginPct = totalInvoiced > 0 ? ((projectProfitLoss / totalInvoiced) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/projects')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="project-detail-title">{project.name}</h1>
          <p className="text-sm text-slate-500">{project.client?.name} &middot; {project.description}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowProjectEdit(true)} data-testid="edit-project-button">
          <Pencil className="w-4 h-4 mr-1" /> Edit Project
        </Button>
        <Badge className={`${getStatusColor(project.status)}`}>{project.status}</Badge>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <SummaryCard label="Quoted" value={formatCurrency(totalQuoted)} icon={FileText} />
        <SummaryCard label="Estimated" value={formatCurrency(totalEstimated)} icon={Calculator} />
        <SummaryCard label="Invoiced" value={formatCurrency(totalInvoiced)} icon={Receipt} />
        <SummaryCard label="Collected" value={formatCurrency(totalPaid)} icon={CreditCard} />
        <SummaryCard label="Outstanding" value={formatCurrency(totalOutstanding)} icon={History} />
        <SummaryCard label="Expenses" value={formatCurrency(totalExpenses)} icon={DollarSign} />
        <SummaryCard label="Profit / Loss" value={formatCurrency(projectProfitLoss)} icon={TrendingUp} />
        <SummaryCard label="Margin" value={`${projectMarginPct.toFixed(1)}%`} icon={BarChart3} />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-auto flex-wrap justify-start bg-white border border-slate-200">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="service-notes" data-testid="tab-service-notes">
            <ClipboardList className="mr-1 h-4 w-4" /> Service Notes
          </TabsTrigger>
          <TabsTrigger value="quotes" data-testid="tab-quotes">Quotes ({quotes.length})</TabsTrigger>
          <TabsTrigger value="estimates" data-testid="tab-estimates">Estimates ({estimates.length})</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments ({paymentActivity.length})</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses ({expenses.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-y-4">
                <InfoRow label="Client" value={project.client?.name} />
                <InfoRow label="Status" value={project.status} />
                <InfoRow label="Start Date" value={formatDate(project.start_date)} />
                <InfoRow label="End Date" value={formatDate(project.end_date)} />
                <InfoRow label="Description" value={project.description || 'No description'} />
                <InfoRow label="Created" value={formatDate(project.created_at)} />
                <InfoRow label="Default PO #" value={project.default_client_po_number || 'Not set'} />
                <InfoRow label="Default Ticket #" value={project.default_client_ticket_number || 'Not set'} />
                <InfoRow label="Invoiced Total" value={formatCurrency(totalInvoiced)} />
                <InfoRow label="Project Expenses" value={formatCurrency(totalExpenses)} />
                <InfoRow label="Profit / Loss" value={formatCurrency(projectProfitLoss)} />
                <InfoRow label="Outstanding" value={formatCurrency(totalOutstanding)} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="service-notes" className="mt-4">
          <ServiceNotesPanel project={project} />
        </TabsContent>

        {/* QUOTES TAB */}
        <TabsContent value="quotes" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setEditingQuote(null); setShowQuoteDialog(true); }} data-testid="add-quote-button">
              <Plus className="w-4 h-4 mr-2" /> New Quote
            </Button>
          </div>
          {quotes.length === 0 ? (
            <Card className="border border-slate-200"><CardContent className="p-8 text-center text-slate-500">No quotes yet</CardContent></Card>
          ) : (
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Rev</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Invoiced</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotes.map(q => (
                      <TableRow key={q.id} onClick={() => setHistoryQuoteId(q.id)} className="cursor-pointer hover:bg-slate-50">
                        <TableCell className="font-medium">{q.quote_number}</TableCell>
                        <TableCell>{q.title}</TableCell>
                        <TableCell>v{q.revision}</TableCell>
                        <TableCell><Badge className={`text-xs ${getStatusColor(q.status)}`}>{q.status}</Badge></TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(q.total)}</TableCell>
                        <TableCell className="text-right text-slate-500">{formatCurrency(q.total_invoiced)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(event) => { event.stopPropagation(); previewDocumentPdf('quote', q.id, q.quote_number); }}
                              title="Preview PDF / PDF-Vorschau"
                              data-testid={`preview-quote-${q.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={(event) => {
                                event.stopPropagation();
                                setHistoryQuoteId(q.id);
                                setSendQuoteModal({ open: true, quoteId: q.id, quoteNumber: q.quote_number });
                              }}
                              title="Send quote"
                              data-testid={`send-quote-${q.id}`}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={(event) => { event.stopPropagation(); setEditingQuote(q); setShowQuoteDialog(true); }}
                              title="Edit quote"
                              data-testid={`edit-quote-${q.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          {quotes.length > 0 && activeTab === 'quotes' && historyQuoteId && (
            <>
              <div className="flex items-center gap-3">
                <Label className="text-sm text-slate-600">History for quote</Label>
                <Select value={historyQuoteId} onValueChange={setHistoryQuoteId}>
                  <SelectTrigger className="w-64" data-testid="quote-history-select">
                    <SelectValue placeholder="Select quote" />
                  </SelectTrigger>
                  <SelectContent>
                    {quotes.map((quote) => (
                      <SelectItem key={quote.id} value={quote.id}>
                        {`${quote.quote_number} | ${formatDate(quote.created_at)} | ${String(quote.id).slice(0, 8)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DeliveryHistory
                documentType="quote"
                documentId={historyQuoteId}
                onRefresh={fetchAll}
                refreshKey={deliveryHistoryVersion}
              />
            </>
          )}
        </TabsContent>

        {/* ESTIMATES TAB */}
        <TabsContent value="estimates" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setEditingEstimate(null); setShowEstimateDialog(true); }} data-testid="add-estimate-button">
              <Plus className="w-4 h-4 mr-2" /> New Estimate
            </Button>
          </div>
          {estimates.length === 0 ? (
            <Card className="border border-slate-200"><CardContent className="p-8 text-center text-slate-500">No estimates yet</CardContent></Card>
          ) : (
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Rev</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Labor</TableHead>
                      <TableHead className="text-right">Materials</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {estimates.map(e => (
                      <TableRow key={e.id} onClick={() => setHistoryEstimateId(e.id)} className="cursor-pointer hover:bg-slate-50">
                        <TableCell className="font-medium">{e.estimate_number}</TableCell>
                        <TableCell>{e.title}</TableCell>
                        <TableCell>v{e.revision}</TableCell>
                        <TableCell><Badge className={`text-xs ${getStatusColor(e.status)}`}>{e.status}</Badge></TableCell>
                        <TableCell className="text-right">{formatCurrency(e.labor_subtotal)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(e.materials_subtotal)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(e.total)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(event) => { event.stopPropagation(); previewDocumentPdf('estimate', e.id, e.estimate_number); }}
                              title="Preview PDF / PDF-Vorschau"
                              data-testid={`preview-estimate-${e.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={(event) => {
                                event.stopPropagation();
                                setHistoryEstimateId(e.id);
                                setSendEstimateModal({ open: true, estimateId: e.id, estimateNumber: e.estimate_number });
                              }}
                              title="Send estimate"
                              data-testid={`send-estimate-${e.id}`}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={(event) => { event.stopPropagation(); setEditingEstimate(e); setShowEstimateDialog(true); }}
                              title="Edit estimate"
                              data-testid={`edit-estimate-${e.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          {estimates.length > 0 && activeTab === 'estimates' && historyEstimateId && (
            <>
              <div className="flex items-center gap-3">
                <Label className="text-sm text-slate-600">History for estimate</Label>
                <Select value={historyEstimateId} onValueChange={setHistoryEstimateId}>
                  <SelectTrigger className="w-64" data-testid="estimate-history-select">
                    <SelectValue placeholder="Select estimate" />
                  </SelectTrigger>
                  <SelectContent>
                    {estimates.map((estimate) => (
                      <SelectItem key={estimate.id} value={estimate.id}>
                        {`${estimate.estimate_number} | ${formatDate(estimate.created_at)} | ${String(estimate.id).slice(0, 8)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DeliveryHistory
                documentType="estimate"
                documentId={historyEstimateId}
                onRefresh={fetchAll}
                refreshKey={deliveryHistoryVersion}
              />
            </>
          )}
        </TabsContent>

        {/* INVOICES TAB */}
        <TabsContent value="invoices" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setEditingInvoice(null); setShowInvoiceDialog(true); }} data-testid="add-invoice-button">
              <Plus className="w-4 h-4 mr-2" /> New Invoice
            </Button>
          </div>
          {invoices.length === 0 ? (
            <Card className="border border-slate-200"><CardContent className="p-8 text-center text-slate-500">No invoices yet</CardContent></Card>
          ) : (
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Credits</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map(inv => {
                      const canEdit = inv.status !== 'paid' && inv.status !== 'void';
                      return (
                        <TableRow key={inv.id} onClick={() => setHistoryInvoiceId(inv.id)} className="cursor-pointer hover:bg-slate-50">
                          <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{getInvoiceTypeLabel(inv.invoice_type)}</Badge></TableCell>
                          <TableCell><Badge className={`text-xs ${getStatusColor(inv.status)}`}>{inv.status}</Badge></TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(inv.total)}</TableCell>
                          <TableCell className="text-right text-emerald-600">{formatCurrency(inv.amount_paid)}</TableCell>
                          <TableCell className="text-right text-amber-600">{formatCurrency(inv.credits_applied || 0)}</TableCell>
                          <TableCell className="text-right font-medium text-slate-900">{formatCurrency(inv.balance_due)}</TableCell>
                          <TableCell className="text-sm text-slate-500">{formatDate(inv.due_date)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); previewDocumentPdf('invoice', inv.id, inv.invoice_number); }}
                                title="Preview PDF / PDF-Vorschau"
                                data-testid={`preview-invoice-${inv.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {inv.status !== 'paid' && inv.status !== 'void' && parseFloat(inv.balance_due) > 0 && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => { e.stopPropagation(); setApplyCreditInvoice(inv); }}
                                  title="Apply credit note"
                                  data-testid={`apply-credit-${inv.id}`}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => {
                                  setHistoryInvoiceId(inv.id);
                                  setSendInvoiceModal({ open: true, invoiceId: inv.id, invoiceNumber: inv.invoice_number });
                                }}
                                title="Send invoice"
                                data-testid={`send-invoice-${inv.id}`}
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); setEInvoiceExport(inv); }}
                                title="German e-invoice export"
                                data-testid={`einvoice-export-${inv.id}`}
                              >
                                <FileCode2 className="w-4 h-4" />
                              </Button>
                              {canEdit && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => { setHistoryInvoiceId(inv.id); setEditingInvoice(inv); setShowInvoiceDialog(true); }}
                                  title="Edit invoice"
                                  data-testid={`edit-invoice-${inv.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          {invoices.length > 0 && activeTab === 'invoices' && historyInvoiceId && (
            <>
              <div className="flex items-center gap-3">
                <Label className="text-sm text-slate-600">History for invoice</Label>
                <Select value={historyInvoiceId} onValueChange={setHistoryInvoiceId}>
                  <SelectTrigger className="w-64" data-testid="invoice-history-select">
                    <SelectValue placeholder="Select invoice" />
                  </SelectTrigger>
                  <SelectContent>
                    {invoices.map((inv) => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {`${inv.invoice_number} | ${formatDate(inv.created_at)} | ${String(inv.id).slice(0, 8)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DeliveryHistory 
                documentType="invoice" 
                documentId={historyInvoiceId}
                onRefresh={fetchAll}
                refreshKey={deliveryHistoryVersion}
              />
            </>
          )}
        </TabsContent>

        {/* PAYMENTS TAB */}
        <TabsContent value="payments" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setEditingPayment(null); setShowPaymentDialog(true); }} data-testid="add-payment-button">
              <Plus className="w-4 h-4 mr-2" /> Record Payment
            </Button>
          </div>
          {paymentActivity.length === 0 ? (
            <Card className="border border-slate-200"><CardContent className="p-8 text-center text-slate-500">No payments or credit applications yet</CardContent></Card>
          ) : (
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Applied to</TableHead>
                      <TableHead>Method / Source</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paymentActivity.map((item) => {
                      if (item.kind === 'credit') {
                        const c = item.data;
                        return (
                          <TableRow key={`credit-${c.id}`} data-testid={`credit-application-${c.id}`}>
                            <TableCell>{formatDate(c.applied_at)}</TableCell>
                            <TableCell>
                              <Badge className="bg-amber-100 text-amber-800 text-xs">Credit Note</Badge>
                            </TableCell>
                            <TableCell className="font-medium text-amber-600">{formatCurrency(c.amount_applied)}</TableCell>
                            <TableCell className="text-sm text-slate-700">{c.invoice_number || '—'}</TableCell>
                            <TableCell className="text-sm">{c.credit_note_number}</TableCell>
                            <TableCell>—</TableCell>
                            <TableCell className="text-sm text-slate-500">Credit applied to invoice</TableCell>
                            <TableCell />
                          </TableRow>
                        );
                      }
                      const p = item.data;
                      return (
                        <TableRow key={p.id}>
                          <TableCell>{formatDate(p.payment_date)}</TableCell>
                          <TableCell>
                            <Badge className="bg-emerald-100 text-emerald-800 text-xs">Payment</Badge>
                          </TableCell>
                          <TableCell className="font-medium text-emerald-600">{formatCurrency(p.amount)}</TableCell>
                          <TableCell className="text-sm text-slate-700 max-w-xs">
                            {p.allocations && p.allocations.length > 0
                              ? p.allocations.map((a) => `${a.invoice_number || '?'} (${formatCurrency(a.amount_applied)})`).join(' · ')
                              : '—'}
                          </TableCell>
                          <TableCell>{p.payment_method.replace('_', ' ')}</TableCell>
                          <TableCell>{p.reference_number || '-'}</TableCell>
                          <TableCell className="text-sm text-slate-500">{p.notes || '-'}</TableCell>
                          <TableCell className="text-right">
                            {!p.stripe_payment_intent_id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setEditingPayment(p); setShowPaymentDialog(true); }}
                                title="Edit payment date"
                                data-testid={`edit-payment-${p.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* EXPENSES TAB */}
        <TabsContent value="expenses" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Linked expenses: <span className="font-medium text-slate-900">{expenses.length}</span> | Total: <span className="font-medium text-slate-900">{formatCurrency(totalExpenses)}</span>
            </p>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowExpenseDialog(true)} data-testid="add-expense-button">
              <Plus className="w-4 h-4 mr-2" /> Add Expense
            </Button>
          </div>
          {expenses.length === 0 ? (
            <Card className="border border-slate-200"><CardContent className="p-8 text-center text-slate-500">No expenses yet</CardContent></Card>
          ) : (
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.map(e => (
                      <TableRow key={e.id}>
                        <TableCell>{formatDate(e.expense_date)}</TableCell>
                        <TableCell className="font-medium">{e.description}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{e.category}</Badge></TableCell>
                        <TableCell>{e.vendor || '-'}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(e.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <EditProjectDialog open={showProjectEdit} onClose={() => setShowProjectEdit(false)} project={project} clients={clients} onDone={() => { setShowProjectEdit(false); fetchAll(); }} />
      <QuoteDialog open={showQuoteDialog} onClose={() => setShowQuoteDialog(false)} projectId={projectId} project={project} editQuote={editingQuote} onDone={() => { setShowQuoteDialog(false); setEditingQuote(null); fetchAll(); }} />
      <EstimateDialog open={showEstimateDialog} onClose={() => setShowEstimateDialog(false)} projectId={projectId} project={project} editEstimate={editingEstimate} onDone={() => { setShowEstimateDialog(false); setEditingEstimate(null); fetchAll(); }} />
      <InvoiceDialog open={showInvoiceDialog} onClose={() => setShowInvoiceDialog(false)} projectId={projectId} project={project} quotes={quotes} estimates={estimates} editInvoice={editingInvoice} onDone={() => { setShowInvoiceDialog(false); setEditingInvoice(null); fetchAll(); }} />
      <PaymentDialog
        open={showPaymentDialog}
        onClose={() => { setShowPaymentDialog(false); setEditingPayment(null); }}
        projectId={projectId}
        invoices={invoices}
        editPayment={editingPayment}
        onDone={() => { setShowPaymentDialog(false); setEditingPayment(null); fetchAll(); }}
      />
      <ApplyCreditModal
        open={Boolean(applyCreditInvoice)}
        onClose={() => setApplyCreditInvoice(null)}
        invoice={applyCreditInvoice}
        customerId={project?.client_id}
        onApplied={fetchAll}
      />
      <ExpenseDialog open={showExpenseDialog} onClose={() => setShowExpenseDialog(false)} projectId={projectId} onDone={() => { setShowExpenseDialog(false); fetchAll(); }} />
      
      {/* Send Document Modals */}
      <SendDocumentModal
        open={sendQuoteModal.open}
        onClose={() => setSendQuoteModal({ open: false, quoteId: null, quoteNumber: null })}
        documentType="quote"
        documentId={sendQuoteModal.quoteId}
        documentNumber={sendQuoteModal.quoteNumber}
        onSent={handleDocumentSent}
      />
      <EInvoiceExportModal
        open={!!eInvoiceExport}
        onClose={() => setEInvoiceExport(null)}
        invoice={eInvoiceExport}
        client={project?.client}
      />
      <SendDocumentModal
        open={sendEstimateModal.open}
        onClose={() => setSendEstimateModal({ open: false, estimateId: null, estimateNumber: null })}
        documentType="estimate"
        documentId={sendEstimateModal.estimateId}
        documentNumber={sendEstimateModal.estimateNumber}
        onSent={handleDocumentSent}
      />
      <SendDocumentModal
        open={sendInvoiceModal.open}
        onClose={() => setSendInvoiceModal({ open: false, invoiceId: null, invoiceNumber: null })}
        documentType="invoice"
        documentId={sendInvoiceModal.invoiceId}
        documentNumber={sendInvoiceModal.invoiceNumber}
        onSent={handleDocumentSent}
      />
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon }) {
  return (
    <Card className="border border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-slate-400" />
          <div>
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-lg font-semibold text-slate-900">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-500 font-medium">{label}</p>
      <p className="text-sm text-slate-900">{value || '-'}</p>
    </div>
  );
}

// ─── Edit Project Dialog ───
function EditProjectDialog({ open, onClose, project, clients, onDone }) {
  const [form, setForm] = useState({
    name: '', description: '', status: 'active',
    client_id: '', default_client_po_number: '', default_client_ticket_number: '',
    start_date: '', end_date: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project && open) {
      setForm({
        name: project.name || '',
        description: project.description || '',
        status: project.status || 'active',
        client_id: project.client_id || '',
        default_client_po_number: project.default_client_po_number || '',
        default_client_ticket_number: project.default_client_ticket_number || '',
        start_date: project.start_date ? new Date(project.start_date).toISOString().split('T')[0] : '',
        end_date: project.end_date ? new Date(project.end_date).toISOString().split('T')[0] : '',
      });
    }
  }, [project, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Project name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        status: form.status,
        client_id: form.client_id || undefined,
        default_client_po_number: form.default_client_po_number || null,
        default_client_ticket_number: form.default_client_ticket_number || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      };
      await api.put(`/projects/${project.id}`, payload);
      toast.success('Project updated');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Project Name *</Label>
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Project name" data-testid="edit-project-name" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} placeholder="Project description" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                <SelectTrigger data-testid="edit-project-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger data-testid="edit-project-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} />
            </div>
          </div>
          <div className="border-t pt-4 mt-4">
            <p className="text-sm font-medium text-slate-700 mb-3">Default PO / Ticket for New Documents</p>
            <p className="text-xs text-slate-500 mb-3">These values prefill new quotes, estimates, and invoices. Each document keeps its own editable copy.</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default PO #</Label>
                <Input value={form.default_client_po_number} onChange={e => setForm({...form, default_client_po_number: e.target.value})} placeholder="PO-2024-001" data-testid="edit-project-po" />
              </div>
              <div className="space-y-2">
                <Label>Default Ticket #</Label>
                <Input value={form.default_client_ticket_number} onChange={e => setForm({...form, default_client_ticket_number: e.target.value})} placeholder="TKT-001" data-testid="edit-project-ticket" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="save-project-submit">
              {saving ? 'Saving...' : 'Update Project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Quote Dialog ───
function QuoteDialog({ open, onClose, projectId, project, editQuote, onDone }) {
  const [taxTreatment, setTaxTreatment] = useState('standard');
  const [form, setForm] = useState({ 
    quote_date: new Date().toISOString().split('T')[0],
    title: '', 
    description: '', 
    notes: '', 
    terms: '', 
    client_po_number: '', 
    client_ticket_number: '' 
  });
  const [lineItems, setLineItems] = useState([{ 
    description: '', 
    quantity: '1', 
    unit_price: '0', 
    service_date: new Date().toISOString().split('T')[0],
    taxable_gst: taxTreatment !== 'de_b2b_third_country_service',
    taxable_pst: taxTreatment !== 'de_b2b_third_country_service',
  }]);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load existing quote data when editing
  useEffect(() => {
    if (editQuote && open) {
      const quoteDate = editQuote.quote_date ? new Date(editQuote.quote_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      setForm({
        quote_date: quoteDate,
        title: editQuote.title || '',
        description: editQuote.description || '',
        notes: editQuote.notes || '',
        terms: editQuote.terms || '',
        client_po_number: editQuote.client_po_number || '',
        client_ticket_number: editQuote.client_ticket_number || '',
      });
      setTaxTreatment(documentTaxTreatment(editQuote.line_items));
      
      // Load line items with service_dates
      if (editQuote.line_items && editQuote.line_items.length > 0) {
        setLineItems(editQuote.line_items.map(li => ({
          position_number: li.position_number,
          description: li.description || '',
          quantity: String(li.quantity || 1),
          unit_price: String(li.unit_price || 0),
          service_date: li.service_date ? new Date(li.service_date).toISOString().split('T')[0] : quoteDate,
          taxable_gst: li.taxable_gst !== undefined ? li.taxable_gst : true,
          taxable_pst: li.taxable_pst !== undefined ? li.taxable_pst : false,
          tax_rule_id: li.tax_rule_id || null,
          tax_code: li.tax_code || null,
          tax_exemption_reason: li.tax_exemption_reason || '',
        })));
      }
    } else if (!editQuote && open) {
      const today = new Date().toISOString().split('T')[0];
      setForm({ 
        quote_date: today, title: '', description: '', notes: '', terms: '', 
        client_po_number: project?.default_client_po_number || '', 
        client_ticket_number: project?.default_client_ticket_number || '' 
      });
      setTaxTreatment('standard');
      setLineItems([{ description: '', quantity: '1', unit_price: '0', service_date: today, taxable_gst: true, taxable_pst: true }]);
    }
  }, [editQuote, open, project]);

  const addLine = () => setLineItems([...lineItems, { 
    description: '', 
    quantity: '1', 
    unit_price: '0', 
    service_date: form.quote_date,  // Default to quote_date
    taxable_gst: taxTreatment !== 'de_b2b_third_country_service',
    taxable_pst: taxTreatment !== 'de_b2b_third_country_service',
  }]);
  
  const removeLine = (i) => setLineItems(lineItems.filter((_, idx) => idx !== i));
  
  const updateLine = (i, field, value) => {
    const items = [...lineItems];
    items[i] = { ...items[i], [field]: value };
    setLineItems(items);
  };

  const changeTaxTreatment = (value) => {
    setTaxTreatment(value);
    if (value === 'de_b2b_third_country_service') {
      setLineItems((items) => items.map((item) => ({ ...item, taxable_gst: false, taxable_pst: false })));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Title is required'); return; }
    if (lineItems.some(li => !li.description)) { toast.error('All line items need a description'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        line_items: lineItems.map((li, i) => applyTaxTreatment({
          position_number: li.position_number || undefined,
          description: li.description,
          quantity: parseFloat(li.quantity) || 1,
          unit_price: parseFloat(li.unit_price) || 0,
          service_date: li.service_date || form.quote_date,
          taxable_gst: li.taxable_gst,
          taxable_pst: li.taxable_pst,
          tax_rule_id: li.tax_rule_id || null,
          tax_code: li.tax_code || null,
          tax_exemption_reason: li.tax_exemption_reason || '',
          sort_order: i,
        }, taxTreatment)),
      };
      
      if (editQuote) {
        // Update existing quote
        await api.put(`/quotes/${editQuote.id}`, payload);
        toast.success('Quote updated');
      } else {
        // Create new quote
        await api.post('/quotes', { project_id: projectId, ...payload });
        toast.success('Quote created');
      }
      
      setForm({ title: '', description: '', notes: '', terms: '', client_po_number: '', client_ticket_number: '' });
      setLineItems([{ description: '', quantity: '1', unit_price: '0', taxable_gst: true, taxable_pst: true }]);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${editQuote ? 'update' : 'create'} quote`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editQuote ? 'Edit Quote' : 'Create Quote (Fixed Price)'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Quote Date *</Label>
              <Input 
                type="date" 
                value={form.quote_date} 
                onChange={e => setForm({...form, quote_date: e.target.value})}
                data-testid="quote-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Bathroom Renovation Package" data-testid="quote-title" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} />
          </div>

          <TaxTreatmentSelector value={taxTreatment} onChange={changeTaxTreatment} />

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="font-semibold">Line Items</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setShowTemplateSelector(true)} data-testid="quote-add-from-templates">
                  <Package className="w-3 h-3 mr-1" />Templates
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={addLine}><Plus className="w-3 h-3 mr-1" />Add Line</Button>
              </div>
            </div>
            <div className="space-y-2">
              {lineItems.map((li, i) => (
                <div key={i} className="space-y-2 border border-slate-200 p-3 rounded-md">
                  <div className="flex gap-2 items-start">
                    <Input className="flex-1" placeholder="Description" value={li.description} onChange={e => updateLine(i, 'description', e.target.value)} />
                    <Input className="w-20" type="number" placeholder="Qty" value={li.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} />
                    <Input className="w-28" type="number" step="0.01" placeholder="Price" value={li.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)} />
                    <Input 
                      className="w-32" 
                      type="date" 
                      value={li.service_date || form.quote_date} 
                      onChange={e => updateLine(i, 'service_date', e.target.value)}
                      data-testid={`quote-line-service-date-${i}`}
                    />
                    <div className="text-right w-24 pt-2 text-sm font-medium">
                      {formatCurrency((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0))}
                    </div>
                    {lineItems.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeLine(i)}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
                    )}
                  </div>
                  <div className="flex gap-4 items-center text-sm">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={li.taxable_gst} 
                        onChange={e => updateLine(i, 'taxable_gst', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-slate-600">GST (5%)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={li.taxable_pst} 
                        onChange={e => updateLine(i, 'taxable_pst', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-slate-600">PST (7%)</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals Summary */}
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal:</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(lineItems.reduce((sum, li) => sum + ((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0)), 0))}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">GST (5%):</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(lineItems.reduce((sum, li) => {
                    if (li.taxable_gst) {
                      return sum + ((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0) * 0.05);
                    }
                    return sum;
                  }, 0))}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">PST (7%):</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(lineItems.reduce((sum, li) => {
                    if (li.taxable_pst) {
                      return sum + ((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0) * 0.07);
                    }
                    return sum;
                  }, 0))}
                </span>
              </div>
              <div className="flex justify-between text-base font-bold border-t border-slate-300 pt-2 mt-2">
                <span className="text-slate-900">Total:</span>
                <span className="text-slate-900">
                  {formatCurrency(lineItems.reduce((sum, li) => {
                    const amount = (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0);
                    const gst = li.taxable_gst ? amount * 0.05 : 0;
                    const pst = li.taxable_pst ? amount * 0.07 : 0;
                    return sum + amount + gst + pst;
                  }, 0))}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Client PO #</Label>
              <Input value={form.client_po_number} onChange={e => setForm({...form, client_po_number: e.target.value})} placeholder="PO-2024-001 (optional)" data-testid="quote-po-input" />
            </div>
            <div className="space-y-2">
              <Label>Client Ticket #</Label>
              <Input value={form.client_ticket_number} onChange={e => setForm({...form, client_ticket_number: e.target.value})} placeholder="TKT-001 (optional)" data-testid="quote-ticket-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
            <div className="space-y-2"><Label>Terms</Label><Textarea value={form.terms} onChange={e => setForm({...form, terms: e.target.value})} rows={2} /></div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="create-quote-submit">
              {saving ? (editQuote ? 'Updating...' : 'Creating...') : (editQuote ? 'Update Quote' : 'Create Quote')}
            </Button>
          </DialogFooter>
        </form>
        <TemplateSelector 
          open={showTemplateSelector} 
          onClose={() => setShowTemplateSelector(false)}
          documentDate={form.quote_date}
          onSelectTemplates={(templates) => {
            setLineItems([...lineItems, ...templates.map((item) => applyTaxTreatment(item, taxTreatment))]);
            setShowTemplateSelector(false);
          }} 
        />
      </DialogContent>
    </Dialog>
  );
}

// ─── Estimate Dialog ───
function EstimateDialog({ open, onClose, projectId, project, editEstimate, onDone }) {
  const [taxTreatment, setTaxTreatment] = useState('standard');
  const [form, setForm] = useState({ 
    estimate_date: new Date().toISOString().split('T')[0],
    title: '', 
    description: '', 
    notes: '', 
    terms: '', 
    client_po_number: '', 
    client_ticket_number: '' 
  });
  const [lineItems, setLineItems] = useState([{ 
    item_type: 'labor', 
    description: '', 
    quantity: '1', 
    unit_price: '0', 
    service_date: new Date().toISOString().split('T')[0],
    taxable_gst: true, 
    taxable_pst: true 
  }]);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load existing estimate data when editing
  useEffect(() => {
    if (editEstimate && open) {
      const estimateDate = editEstimate.estimate_date ? new Date(editEstimate.estimate_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      setForm({
        estimate_date: estimateDate,
        title: editEstimate.title || '',
        description: editEstimate.description || '',
        notes: editEstimate.notes || '',
        terms: editEstimate.terms || '',
        client_po_number: editEstimate.client_po_number || '',
        client_ticket_number: editEstimate.client_ticket_number || '',
      });
      setTaxTreatment(documentTaxTreatment(editEstimate.line_items));
      
      // Load line items with service_dates
      if (editEstimate.line_items && editEstimate.line_items.length > 0) {
        setLineItems(editEstimate.line_items.map(li => ({
          position_number: li.position_number,
          item_type: li.item_type || 'labor',
          description: li.description || '',
          quantity: String(li.quantity || 1),
          unit_price: String(li.unit_price || 0),
          service_date: li.service_date ? new Date(li.service_date).toISOString().split('T')[0] : estimateDate,
          taxable_gst: li.taxable_gst !== undefined ? li.taxable_gst : true,
          taxable_pst: li.taxable_pst !== undefined ? li.taxable_pst : false,
          tax_rule_id: li.tax_rule_id || null,
          tax_code: li.tax_code || null,
          tax_exemption_reason: li.tax_exemption_reason || '',
        })));
      }
    } else if (!editEstimate && open) {
      const today = new Date().toISOString().split('T')[0];
      setForm({ 
        estimate_date: today, title: '', description: '', notes: '', terms: '', 
        client_po_number: project?.default_client_po_number || '', 
        client_ticket_number: project?.default_client_ticket_number || '' 
      });
      setTaxTreatment('standard');
      setLineItems([{ item_type: 'labor', description: '', quantity: '1', unit_price: '0', service_date: today, taxable_gst: true, taxable_pst: true }]);
    }
  }, [editEstimate, open, project]);

  const addLine = () => setLineItems([...lineItems, { 
    item_type: 'labor', 
    description: '', 
    quantity: '1', 
    unit_price: '0', 
    service_date: form.estimate_date,  // Default to estimate_date
    taxable_gst: taxTreatment !== 'de_b2b_third_country_service',
    taxable_pst: taxTreatment !== 'de_b2b_third_country_service',
  }]);
  
  const removeLine = (i) => setLineItems(lineItems.filter((_, idx) => idx !== i));
  
  const updateLine = (i, field, value) => {
    const items = [...lineItems];
    items[i] = { ...items[i], [field]: value };
    setLineItems(items);
  };

  const changeTaxTreatment = (value) => {
    setTaxTreatment(value);
    if (value === 'de_b2b_third_country_service') {
      setLineItems((items) => items.map((item) => ({ ...item, taxable_gst: false, taxable_pst: false })));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        line_items: lineItems.map((li, i) => applyTaxTreatment({
          position_number: li.position_number || undefined,
          item_type: li.item_type,
          description: li.description,
          quantity: parseFloat(li.quantity) || 1,
          unit_price: parseFloat(li.unit_price) || 0,
          service_date: li.service_date || form.estimate_date,
          taxable_gst: li.taxable_gst,
          taxable_pst: li.taxable_pst,
          tax_rule_id: li.tax_rule_id || null,
          tax_code: li.tax_code || null,
          tax_exemption_reason: li.tax_exemption_reason || '',
          sort_order: i,
        }, taxTreatment)),
      };
      
      if (editEstimate) {
        // Update existing estimate
        await api.put(`/estimates/${editEstimate.id}`, payload);
        toast.success('Estimate updated');
      } else {
        // Create new estimate
        await api.post('/estimates', { project_id: projectId, ...payload });
        toast.success('Estimate created');
      }
      
      setForm({ title: '', description: '', notes: '', terms: '', client_po_number: '', client_ticket_number: '' });
      setLineItems([{ item_type: 'labor', description: '', quantity: '1', unit_price: '0', taxable_gst: true, taxable_pst: true }]);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${editEstimate ? 'update' : 'create'} estimate`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editEstimate ? 'Edit Estimate' : 'Create Estimate (Labor + Materials)'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Estimate Date *</Label>
              <Input 
                type="date" 
                value={form.estimate_date} 
                onChange={e => setForm({...form, estimate_date: e.target.value})}
                data-testid="estimate-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Plumbing Work Estimate" data-testid="estimate-title" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} />
          </div>

          <TaxTreatmentSelector value={taxTreatment} onChange={changeTaxTreatment} />

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="font-semibold">Line Items</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setShowTemplateSelector(true)} data-testid="estimate-add-from-templates">
                  <Package className="w-3 h-3 mr-1" />Templates
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={addLine}><Plus className="w-3 h-3 mr-1" />Add Line</Button>
              </div>
            </div>
            <div className="space-y-2">
              {lineItems.map((li, i) => (
                <div key={i} className="space-y-2 border border-slate-200 p-3 rounded-md">
                  <div className="flex gap-2 items-start">
                    <Select value={li.item_type} onValueChange={v => updateLine(i, 'item_type', v)}>
                      <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="labor">Labor</SelectItem>
                        <SelectItem value="material">Material</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input className="flex-1" placeholder="Description" value={li.description} onChange={e => updateLine(i, 'description', e.target.value)} />
                    <Input className="w-20" type="number" placeholder="Qty" value={li.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} />
                    <Input className="w-28" type="number" step="0.01" placeholder="Price" value={li.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)} />
                    <Input 
                      className="w-32" 
                      type="date" 
                      value={li.service_date || form.estimate_date} 
                      onChange={e => updateLine(i, 'service_date', e.target.value)}
                      data-testid={`estimate-line-service-date-${i}`}
                    />
                    <div className="text-right w-24 pt-2 text-sm font-medium">
                      {formatCurrency((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0))}
                    </div>
                    {lineItems.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeLine(i)}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
                    )}
                  </div>
                  <div className="flex gap-4 items-center text-sm">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={li.taxable_gst} 
                        onChange={e => updateLine(i, 'taxable_gst', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-slate-600">GST (5%)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={li.taxable_pst} 
                        onChange={e => updateLine(i, 'taxable_pst', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-slate-600">PST (7%)</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals Summary */}
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Labor Subtotal:</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(lineItems.reduce((sum, li) => {
                    if (li.item_type === 'labor') return sum + ((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0));
                    return sum;
                  }, 0))}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Materials Subtotal:</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(lineItems.reduce((sum, li) => {
                    if (li.item_type === 'material') return sum + ((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0));
                    return sum;
                  }, 0))}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal:</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(lineItems.reduce((sum, li) => sum + ((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0)), 0))}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">GST (5%):</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(lineItems.reduce((sum, li) => {
                    if (li.taxable_gst) {
                      return sum + ((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0) * 0.05);
                    }
                    return sum;
                  }, 0))}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">PST (7%):</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(lineItems.reduce((sum, li) => {
                    if (li.taxable_pst) {
                      return sum + ((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0) * 0.07);
                    }
                    return sum;
                  }, 0))}
                </span>
              </div>
              <div className="flex justify-between text-base font-bold border-t border-slate-300 pt-2 mt-2">
                <span className="text-slate-900">Total:</span>
                <span className="text-slate-900">
                  {formatCurrency(lineItems.reduce((sum, li) => {
                    const amount = (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0);
                    const gst = li.taxable_gst ? amount * 0.05 : 0;
                    const pst = li.taxable_pst ? amount * 0.07 : 0;
                    return sum + amount + gst + pst;
                  }, 0))}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Client PO #</Label>
              <Input value={form.client_po_number} onChange={e => setForm({...form, client_po_number: e.target.value})} placeholder="PO-2024-001 (optional)" data-testid="estimate-po-input" />
            </div>
            <div className="space-y-2">
              <Label>Client Ticket #</Label>
              <Input value={form.client_ticket_number} onChange={e => setForm({...form, client_ticket_number: e.target.value})} placeholder="TKT-001 (optional)" data-testid="estimate-ticket-input" />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="create-estimate-submit">
              {saving ? (editEstimate ? 'Updating...' : 'Creating...') : (editEstimate ? 'Update Estimate' : 'Create Estimate')}
            </Button>
          </DialogFooter>
        </form>
        <TemplateSelector 
          open={showTemplateSelector} 
          onClose={() => setShowTemplateSelector(false)}
          documentDate={form.estimate_date}
          onSelectTemplates={(templates) => {
            setLineItems([...lineItems, ...templates.map((item) => applyTaxTreatment(item, taxTreatment))]);
            setShowTemplateSelector(false);
          }} 
        />
      </DialogContent>
    </Dialog>
  );
}

// ─── Invoice Dialog ───
function InvoiceDialog({ open, onClose, projectId, project, quotes, estimates, editInvoice, onDone }) {
  const [taxTreatment, setTaxTreatment] = useState('standard');
  const [form, setForm] = useState({
    invoice_type: 'progress', source_type: 'quote', source_id: '',
    invoice_percentage: '0.25', payment_terms: 'Net 30', notes: '',
    client_po_number: '', client_ticket_number: '',
    status: 'draft',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: ''
  });
  const [positionSelections, setPositionSelections] = useState([]);
  const [showCompletedPositions, setShowCompletedPositions] = useState(false);
  const [manualItems, setManualItems] = useState([{ 
    position_number: 1,
    description: '', 
    quantity: '1', 
    unit_price: '0', 
    service_date: new Date().toISOString().split('T')[0],
    taxable_gst: true, 
    taxable_pst: true 
  }]);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [saving, setSaving] = useState(false);

  const sourceDocument = useMemo(() => {
    if (form.source_type === 'quote' && form.source_id) {
      return quotes.find((q) => q.id === form.source_id);
    }
    if (form.source_type === 'estimate' && form.source_id) {
      return estimates.find((e) => e.id === form.source_id);
    }
    return null;
  }, [form.source_type, form.source_id, quotes, estimates]);

  const showPositionBilling = !editInvoice && form.source_type !== 'none' && form.source_id && usesPositionBilling(form.invoice_type);
  const showPercentageBilling = !editInvoice && form.source_type !== 'none' && form.source_id && usesPercentageBilling(form.invoice_type);

  const completedPositionCount = useMemo(
    () => positionSelections.filter((row) => row.remaining <= 0).length,
    [positionSelections]
  );

  const visiblePositionRows = useMemo(() => {
    if (showCompletedPositions) return positionSelections;
    return positionSelections.filter((row) => row.remaining > 0);
  }, [positionSelections, showCompletedPositions]);

  const updatePositionRow = (positionNumber, patch) => {
    setPositionSelections((prev) =>
      prev.map((row) =>
        row.position_number === positionNumber ? { ...row, ...patch } : row
      )
    );
  };

  useEffect(() => {
    if (!open || editInvoice || form.source_type === 'none' || !sourceDocument?.line_items) {
      if (!open) {
        setPositionSelections([]);
        setShowCompletedPositions(false);
      }
      return;
    }
    const autoSelectAll = form.invoice_type === 'final';
    const rows = [...sourceDocument.line_items]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((li) => {
        const remaining = lineRemainingQty(li);
        return {
          position_number: li.position_number,
          description: li.description,
          total_qty: parseFloat(li.quantity) || 0,
          qty_invoiced: parseFloat(li.qty_invoiced) || 0,
          remaining,
          selected: autoSelectAll && remaining > 0,
          invoice_qty: remaining > 0 ? String(remaining) : '0',
        };
      });
    setPositionSelections(rows);
  }, [open, editInvoice, form.source_type, form.source_id, form.invoice_type, sourceDocument]);

  useEffect(() => {
    if (editInvoice && open) {
      const invoiceDate = editInvoice.invoice_date ? new Date(editInvoice.invoice_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      const dueDate = editInvoice.due_date ? new Date(editInvoice.due_date).toISOString().split('T')[0] : '';
      
      setForm({
        invoice_type: editInvoice.invoice_type || 'progress',
        source_type: 'none',
        source_id: '',
        invoice_percentage: '0.25',
        payment_terms: editInvoice.payment_terms || 'Net 30',
        notes: editInvoice.notes || '',
        client_po_number: editInvoice.client_po_number || '',
        client_ticket_number: editInvoice.client_ticket_number || '',
        status: editInvoice.status || 'draft',
        invoice_date: invoiceDate,
        due_date: dueDate,
      });
      setTaxTreatment(documentTaxTreatment(editInvoice.line_items));
      
      if (editInvoice.line_items && editInvoice.line_items.length > 0) {
        setManualItems(editInvoice.line_items.map(li => ({
          position_number: li.position_number ?? 1,
          description: li.description || '',
          quantity: String(li.quantity || 1),
          unit_price: String(li.unit_price || 0),
          service_date: li.service_date ? new Date(li.service_date).toISOString().split('T')[0] : invoiceDate,
          taxable_gst: li.taxable_gst !== undefined ? li.taxable_gst : true,
          taxable_pst: li.taxable_pst !== undefined ? li.taxable_pst : false,
          tax_rule_id: li.tax_rule_id || null,
          tax_code: li.tax_code || null,
          tax_exemption_reason: li.tax_exemption_reason || '',
        })));
      }
    } else if (!editInvoice && open) {
      const today = new Date().toISOString().split('T')[0];
      const initialSourceType = quotes?.length > 0 ? 'quote' : (estimates?.length > 0 ? 'estimate' : 'none');
      const initial = defaultSourceForType(initialSourceType, quotes, estimates);
      setForm({
        invoice_type: 'progress', source_type: initial.source_type, source_id: initial.source_id,
        invoice_percentage: '0.25', payment_terms: 'Net 30', notes: '',
        client_po_number: initial.doc?.client_po_number || project?.default_client_po_number || '', 
        client_ticket_number: initial.doc?.client_ticket_number || project?.default_client_ticket_number || '',
        status: 'draft',
        invoice_date: today,
        due_date: ''
      });
      setTaxTreatment('standard');
      setPositionSelections([]);
      setShowCompletedPositions(false);
      setManualItems([{ 
        position_number: 1,
        description: '', 
        quantity: '1', 
        unit_price: '0', 
        service_date: today,
        taxable_gst: true, 
        taxable_pst: true 
      }]);
    }
  }, [editInvoice, open, project, quotes, estimates]);

  const handleInvoiceTypeChange = (invoiceType) => {
    if (invoiceType === 'standard') {
      setForm({ ...form, invoice_type: invoiceType, source_type: 'none', source_id: '' });
      return;
    }
    const sourceType = form.source_type === 'none'
      ? (quotes?.length > 0 ? 'quote' : (estimates?.length > 0 ? 'estimate' : 'none'))
      : form.source_type;
    const next = defaultSourceForType(sourceType, quotes, estimates);
    setForm({
      ...form,
      invoice_type: invoiceType,
      source_type: next.source_type,
      source_id: next.source_id || form.source_id,
      client_po_number: next.doc?.client_po_number || form.client_po_number,
      client_ticket_number: next.doc?.client_ticket_number || form.client_ticket_number,
    });
  };

  const changeTaxTreatment = (value) => {
    setTaxTreatment(value);
    if (value === 'de_b2b_third_country_service') {
      setManualItems((items) => items.map((item) => ({ ...item, taxable_gst: false, taxable_pst: false })));
    }
  };

  const handleSourceTypeChange = (sourceType) => {
    if (sourceType === 'none') {
      setForm({
        ...form,
        source_type: 'none',
        source_id: '',
        invoice_type: 'standard',
        client_po_number: project?.default_client_po_number || '',
        client_ticket_number: project?.default_client_ticket_number || '',
      });
      return;
    }
    const next = defaultSourceForType(sourceType, quotes, estimates);
    setForm({
      ...form,
      source_type: sourceType,
      source_id: next.source_id,
      invoice_type: form.invoice_type === 'standard' ? 'progress' : form.invoice_type,
      client_po_number: next.doc?.client_po_number || form.client_po_number,
      client_ticket_number: next.doc?.client_ticket_number || form.client_ticket_number,
    });
  };

  const sortedQuotes = useMemo(
    () => [...quotes].sort((a, b) => documentTimestamp(b, ['quote_date', 'created_at']) - documentTimestamp(a, ['quote_date', 'created_at'])),
    [quotes]
  );

  const sortedEstimates = useMemo(
    () => [...estimates].sort((a, b) => documentTimestamp(b, ['estimate_date', 'created_at']) - documentTimestamp(a, ['estimate_date', 'created_at'])),
    [estimates]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((editInvoice || form.source_type === 'none') && manualItems.length === 0) {
      toast.error('At least one line item is required');
      return;
    }
    if ((editInvoice || form.source_type === 'none') && manualItems.some(li => !li.description?.trim())) {
      toast.error('All line items need a description');
      return;
    }
    if (showPositionBilling) {
      const picked = positionSelections.filter((row) => row.selected && row.remaining > 0);
      if (picked.length === 0) {
        toast.error('Select at least one position to invoice');
        return;
      }
      for (const row of picked) {
        const qty = parseFloat(row.invoice_qty);
        if (!qty || qty <= 0) {
          toast.error(`Position ${row.position_number} needs a positive quantity`);
          return;
        }
        if (qty > row.remaining + 0.0001) {
          toast.error(`Position ${row.position_number} exceeds remaining quantity`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = {
        payment_terms: form.payment_terms,
        notes: form.notes,
        client_po_number: form.client_po_number || null,
        client_ticket_number: form.client_ticket_number || null,
        invoice_date: form.invoice_date || null,
        due_date: form.due_date || null,
      };

      if (editInvoice) {
        payload.status = form.status;
        payload.line_items = manualItems.map((li, i) => applyTaxTreatment({
          position_number: li.position_number,
          description: li.description,
          quantity: parseFloat(li.quantity) || 1,
          unit_price: parseFloat(li.unit_price) || 0,
          service_date: li.service_date || form.invoice_date,
          taxable_gst: li.taxable_gst,
          taxable_pst: li.taxable_pst,
          tax_rule_id: li.tax_rule_id || null,
          tax_code: li.tax_code || null,
          tax_exemption_reason: li.tax_exemption_reason || '',
          sort_order: i,
        }, taxTreatment));
        
        await api.put(`/invoices/${editInvoice.id}`, payload);
        toast.success('Invoice updated');
      } else {
        payload.project_id = projectId;
        payload.invoice_type = form.invoice_type;
        
        if (form.source_type === 'quote' && form.source_id) {
          payload.source_quote_id = form.source_id;
        } else if (form.source_type === 'estimate' && form.source_id) {
          payload.source_estimate_id = form.source_id;
        } else {
          payload.line_items = manualItems.map((li, i) => applyTaxTreatment({
            position_number: li.position_number ?? (i + 1),
            description: li.description,
            quantity: parseFloat(li.quantity) || 1,
            unit_price: parseFloat(li.unit_price) || 0,
            service_date: li.service_date || form.invoice_date,
            taxable_gst: li.taxable_gst,
            taxable_pst: li.taxable_pst,
            tax_rule_id: li.tax_rule_id || null,
            tax_code: li.tax_code || null,
            tax_exemption_reason: li.tax_exemption_reason || '',
            sort_order: i,
          }, taxTreatment));
        }

        if (showPositionBilling) {
          payload.selected_positions = positionSelections
            .filter((row) => row.selected && row.remaining > 0)
            .map((row) => ({
              position_number: row.position_number,
              quantity: parseFloat(row.invoice_qty) || undefined,
            }));
        } else if (showPercentageBilling) {
          payload.invoice_percentage = parseFloat(form.invoice_percentage) || 1;
        }
        
        await api.post('/invoices', payload);
        toast.success('Invoice created');
      }
      
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${editInvoice ? 'update' : 'create'} invoice`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editInvoice ? 'Edit Invoice' : 'Create Invoice'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editInvoice && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Invoice Type</Label>
                <Select value={form.invoice_type} onValueChange={handleInvoiceTypeChange}>
                  <SelectTrigger data-testid="invoice-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="progress">Progress (by positions)</SelectItem>
                    <SelectItem value="standard">Standard (manual items)</SelectItem>
                    <SelectItem value="deposit">Deposit (%)</SelectItem>
                    <SelectItem value="milestone">Milestone (by positions)</SelectItem>
                    <SelectItem value="final">Final (by positions)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Source Document</Label>
                <Select
                  value={form.source_type}
                  disabled={form.invoice_type === 'standard'}
                  onValueChange={handleSourceTypeChange}
                >
                  <SelectTrigger data-testid="invoice-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quote">From Quote</SelectItem>
                    <SelectItem value="estimate">From Estimate</SelectItem>
                    <SelectItem value="none">Manual (no source)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {!editInvoice && form.source_type === 'quote' && (
            <div className="space-y-2">
              <Label>Select Quote</Label>
              <Select value={form.source_id} onValueChange={v => {
                const srcQuote = quotes.find(q => q.id === v);
                setForm({...form, source_id: v, 
                  client_po_number: srcQuote?.client_po_number || form.client_po_number,
                  client_ticket_number: srcQuote?.client_ticket_number || form.client_ticket_number,
                });
              }}>
                <SelectTrigger data-testid="invoice-quote-select"><SelectValue placeholder="Choose a quote" /></SelectTrigger>
                <SelectContent>
                  {sortedQuotes.map(q => (
                    <SelectItem key={q.id} value={q.id}>
                      {q.quote_number} - {q.title} ({formatCurrency(q.total)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {!editInvoice && form.source_type === 'estimate' && (
            <div className="space-y-2">
              <Label>Select Estimate</Label>
              <Select value={form.source_id} onValueChange={v => {
                const srcEstimate = estimates.find(e => e.id === v);
                setForm({...form, source_id: v,
                  client_po_number: srcEstimate?.client_po_number || form.client_po_number,
                  client_ticket_number: srcEstimate?.client_ticket_number || form.client_ticket_number,
                });
              }}>
                <SelectTrigger data-testid="invoice-estimate-select"><SelectValue placeholder="Choose an estimate" /></SelectTrigger>
                <SelectContent>
                  {sortedEstimates.map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.estimate_number} - {e.title} ({formatCurrency(e.total)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {showPositionBilling && (
            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <Label className="text-base font-semibold">
                {form.source_type === 'estimate' ? 'Estimate' : 'Quote'} Positions
              </Label>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-slate-600">
                  Select positions and quantities to include on this invoice.
                </p>
                {completedPositionCount > 0 && (
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer shrink-0">
                    <Checkbox
                      checked={showCompletedPositions}
                      onCheckedChange={(checked) => setShowCompletedPositions(!!checked)}
                      data-testid="invoice-show-completed-positions"
                    />
                    Show completed positions ({completedPositionCount})
                  </label>
                )}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16 text-center">Include</TableHead>
                    <TableHead className="w-12 text-center">Position #</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right w-24">Original Qty</TableHead>
                    <TableHead className="text-right w-24">Already Invoiced</TableHead>
                    <TableHead className="text-right w-24">Remaining Qty</TableHead>
                    <TableHead className="text-right w-28">Qty to Invoice</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visiblePositionRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-sm text-slate-500 py-6">
                        All positions on this document are fully invoiced.
                        {completedPositionCount > 0 && !showCompletedPositions && (
                          <> Enable &ldquo;Show completed positions&rdquo; to review them.</>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : visiblePositionRows.map((row) => {
                    const hasInvoiced = (row.qty_invoiced || 0) > 0;
                    const fullyInvoiced = row.remaining <= 0;
                    return (
                    <TableRow
                      key={row.position_number}
                      className={fullyInvoiced ? 'opacity-50 bg-slate-100/80' : ''}
                    >
                      <TableCell className="text-center">
                        <Checkbox
                          checked={row.selected && row.remaining > 0}
                          disabled={fullyInvoiced}
                          onCheckedChange={(checked) => {
                            updatePositionRow(row.position_number, { selected: !!checked });
                          }}
                          data-testid={`invoice-position-select-${row.position_number}`}
                        />
                      </TableCell>
                      <TableCell className="text-center font-medium">{row.position_number}</TableCell>
                      <TableCell>
                        <div className="flex items-start gap-2">
                          {hasInvoiced && (
                            <span
                              className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                                fullyInvoiced ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'
                              }`}
                              title={fullyInvoiced ? 'Fully invoiced' : 'Partially invoiced'}
                              data-testid={`invoice-position-invoiced-mark-${row.position_number}`}
                            >
                              <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                            </span>
                          )}
                          <span>{row.description}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{row.total_qty}</TableCell>
                      <TableCell className={`text-right ${hasInvoiced ? 'font-medium text-slate-700' : 'text-slate-500'}`}>
                        {row.qty_invoiced}
                      </TableCell>
                      <TableCell className="text-right">{row.remaining}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          className="w-24 ml-auto"
                          type="number"
                          step="0.01"
                          min="0"
                          max={row.remaining}
                          disabled={!row.selected || fullyInvoiced}
                          value={row.invoice_qty}
                          onChange={(e) => {
                            updatePositionRow(row.position_number, { invoice_qty: e.target.value });
                          }}
                          data-testid={`invoice-position-qty-${row.position_number}`}
                        />
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {showPercentageBilling && (
            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <Label className="text-base font-semibold">Deposit Percentage</Label>
              <div className="space-y-2">
                <Label>Invoice Percentage (e.g. 0.25 = 25% deposit)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="1"
                  value={form.invoice_percentage}
                  onChange={(e) => setForm({ ...form, invoice_percentage: e.target.value })}
                  data-testid="invoice-percentage"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Invoice Date *</Label>
              <Input 
                type="date" 
                value={form.invoice_date} 
                onChange={e => setForm({...form, invoice_date: e.target.value})}
                data-testid="invoice-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input 
                type="date" 
                value={form.due_date} 
                onChange={e => setForm({...form, due_date: e.target.value})}
                data-testid="invoice-due-date-input"
              />
            </div>
          </div>

          {editInvoice && (
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="partial_paid">Partial Paid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="void">Void</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {(editInvoice || form.source_type === 'none') && (
            <div>
              <TaxTreatmentSelector value={taxTreatment} onChange={changeTaxTreatment} />
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">Line Items</Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowTemplateSelector(true)} data-testid="invoice-add-from-templates">
                    <Package className="w-3 h-3 mr-1" />Templates
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setManualItems([...manualItems, { position_number: nextPositionNumber(manualItems), description: '', quantity: '1', unit_price: '0', service_date: form.invoice_date, taxable_gst: taxTreatment !== 'de_b2b_third_country_service', taxable_pst: taxTreatment !== 'de_b2b_third_country_service' }])}>
                    <Plus className="w-3 h-3 mr-1" />Add
                  </Button>
                </div>
              </div>
              {manualItems.map((li, i) => (
                <div key={i} className="flex gap-2 items-start mb-2">
                  <Input className="flex-1" placeholder="Description" value={li.description} onChange={e => { const items = [...manualItems]; items[i].description = e.target.value; setManualItems(items); }} />
                  <Input className="w-20" type="number" placeholder="Qty" value={li.quantity} onChange={e => { const items = [...manualItems]; items[i].quantity = e.target.value; setManualItems(items); }} />
                  <Input className="w-28" type="number" step="0.01" placeholder="Price" value={li.unit_price} onChange={e => { const items = [...manualItems]; items[i].unit_price = e.target.value; setManualItems(items); }} />
                  <Input 
                    className="w-32" 
                    type="date" 
                    value={li.service_date || form.invoice_date} 
                    onChange={e => { const items = [...manualItems]; items[i].service_date = e.target.value; setManualItems(items); }}
                    data-testid={`invoice-line-service-date-${i}`}
                  />
                  <div className="text-right w-24 pt-2 text-sm font-medium">
                    {formatCurrency((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0))}
                  </div>
                  {manualItems.length > 1 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setManualItems(manualItems.filter((_, idx) => idx !== i))}
                    >
                      <Trash2 className="w-4 h-4 text-slate-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Client PO #</Label>
              <Input value={form.client_po_number} onChange={e => setForm({...form, client_po_number: e.target.value})} placeholder="PO-2024-001 (optional)" data-testid="invoice-po-input" />
            </div>
            <div className="space-y-2">
              <Label>Client Ticket #</Label>
              <Input value={form.client_ticket_number} onChange={e => setForm({...form, client_ticket_number: e.target.value})} placeholder="TKT-001 (optional)" data-testid="invoice-ticket-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Payment Terms</Label>
              <Input value={form.payment_terms} onChange={e => setForm({...form, payment_terms: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="create-invoice-submit">
              {saving ? (editInvoice ? 'Updating...' : 'Creating...') : (editInvoice ? 'Update Invoice' : 'Create Invoice')}
            </Button>
          </DialogFooter>
        </form>
        <TemplateSelector 
          open={showTemplateSelector} 
          onClose={() => setShowTemplateSelector(false)}
          documentDate={form.invoice_date}
          onSelectTemplates={(templates) => {
            setManualItems([...manualItems, ...templates.map((item) => applyTaxTreatment(item, taxTreatment))]);
            setShowTemplateSelector(false);
          }} 
        />
      </DialogContent>
    </Dialog>
  );
}

// ─── Payment Dialog ───
function PaymentDialog({ open, onClose, projectId, invoices, editPayment, onDone }) {
  const [form, setForm] = useState({
    invoice_id: '',
    amount: '',
    payment_date: localDateInputValue(),
    payment_method: 'e_transfer',
    reference_number: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const selectedInvoice = invoices.find(i => i.id === form.invoice_id);
  const availableInvoices = invoices.filter(i => i.status !== 'paid' && i.status !== 'void');
  const appliedToLabel = editPayment
    ? (editPayment.allocations || [])
        .map(a => a.invoice_number || invoices.find(i => i.id === a.invoice_id)?.invoice_number)
        .filter(Boolean)
        .join(', ') || selectedInvoice?.invoice_number || 'Recorded invoice'
    : '';

  useEffect(() => {
    if (!open) return;
    if (editPayment) {
      setForm({
        invoice_id: editPayment.invoice_id || editPayment.allocations?.[0]?.invoice_id || '',
        amount: String(editPayment.amount ?? ''),
        payment_date: localDateInputValue(editPayment.payment_date),
        payment_method: editPayment.payment_method || 'e_transfer',
        reference_number: editPayment.reference_number || '',
        notes: editPayment.notes || '',
      });
    } else {
      setForm({
        invoice_id: '',
        amount: '',
        payment_date: localDateInputValue(),
        payment_method: 'e_transfer',
        reference_number: '',
        notes: '',
      });
    }
  }, [open, editPayment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.payment_date) { toast.error('Payment date is required'); return; }
    if (!editPayment && (!form.invoice_id || !form.amount)) { toast.error('Invoice and amount required'); return; }
    setSaving(true);
    try {
      const metadata = {
        payment_date: `${form.payment_date}T12:00:00`,
        payment_method: form.payment_method,
        reference_number: form.reference_number,
        notes: form.notes,
      };
      if (editPayment) {
        await api.patch(`/payments/${editPayment.id}`, metadata);
        toast.success('Payment updated');
      } else {
        await api.post('/payments', {
          project_id: projectId,
          invoice_id: form.invoice_id,
          amount: parseFloat(form.amount),
          ...metadata,
        });
        toast.success('Payment recorded');
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${editPayment ? 'update' : 'record'} payment`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editPayment ? 'Edit Payment' : 'Record Payment'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {editPayment ? (
            <div className="space-y-2">
              <Label>Applied To</Label>
              <Input value={appliedToLabel} disabled />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Invoice *</Label>
              <Select value={form.invoice_id} onValueChange={v => setForm({...form, invoice_id: v})}>
                <SelectTrigger data-testid="payment-invoice-select"><SelectValue placeholder="Select invoice" /></SelectTrigger>
                <SelectContent>
                  {availableInvoices.map(inv => (
                    <SelectItem key={inv.id} value={inv.id}>
                      {inv.invoice_number} - Balance: {formatCurrency(inv.balance_due)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedInvoice && (
                <p className="text-xs text-slate-500">Balance due: {formatCurrency(selectedInvoice.balance_due)}</p>
              )}
            </div>
          )}
          <div className="space-y-2">
            <Label>Amount *</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="0.00" disabled={!!editPayment} data-testid="payment-amount" />
          </div>
          <div className="space-y-2">
            <Label>Payment Date *</Label>
            <Input type="date" value={form.payment_date} onChange={e => setForm({...form, payment_date: e.target.value})} required data-testid="payment-date" />
          </div>
          <div className="space-y-2">
            <Label>Payment Method</Label>
            <Select value={form.payment_method} onValueChange={v => setForm({...form, payment_method: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="e_transfer">E-Transfer</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="credit_card">Credit Card</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Reference #</Label>
            <Input value={form.reference_number} onChange={e => setForm({...form, reference_number: e.target.value})} placeholder="ET-2024-001" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="create-payment-submit">
              {saving ? (editPayment ? 'Updating...' : 'Recording...') : (editPayment ? 'Update Payment' : 'Record Payment')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Expense Dialog ───
function ExpenseDialog({ open, onClose, projectId, onDone }) {
  const [form, setForm] = useState({ category: 'materials', description: '', amount: '', tax_amount: '0', vendor: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) { toast.error('Description and amount required'); return; }
    setSaving(true);
    try {
      await api.post('/expenses', {
        project_id: projectId,
        category: form.category,
        description: form.description,
        amount: parseFloat(form.amount),
        tax_amount: parseFloat(form.tax_amount) || 0,
        vendor: form.vendor,
        notes: form.notes,
      });
      toast.success('Expense added');
      setForm({ category: 'materials', description: '', amount: '', tax_amount: '0', vendor: '', notes: '' });
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add Expense</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="materials">Materials</SelectItem>
                <SelectItem value="labor">Labor</SelectItem>
                <SelectItem value="subcontractor">Subcontractor</SelectItem>
                <SelectItem value="equipment">Equipment</SelectItem>
                <SelectItem value="travel">Travel</SelectItem>
                <SelectItem value="office">Office</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Description *</Label>
            <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Bathroom tiles" data-testid="expense-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="expense-amount" />
            </div>
            <div className="space-y-2">
              <Label>Tax Amount</Label>
              <Input type="number" step="0.01" value={form.tax_amount} onChange={e => setForm({...form, tax_amount: e.target.value})} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Vendor</Label>
            <Input value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})} placeholder="Vancouver Tile Co" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="create-expense-submit">
              {saving ? 'Adding...' : 'Add Expense'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatCurrencyLocal(v) { return formatCurrency(v); }
